/** Awareness payload every client publishes (members and guests alike). */
export interface PresenceUser {
  id: string;
  name: string;
  kind: 'member' | 'guest';
  color: string;
}

export interface PresenceState {
  user: PresenceUser;
  /** Cursor in world coordinates, null when the pointer left the canvas. */
  cursor: { x: number; y: number } | null;
  selection: string[];
}

export interface Peer extends PresenceState {
  clientId: number;
}

const PALETTE = [
  '#e5484d',
  '#f76b15',
  '#ffc53d',
  '#30a46c',
  '#12a594',
  '#0090ff',
  '#6e56cf',
  '#d6409f',
];

/** Stable colour per user id. */
export function colorForId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

function isPresence(v: unknown): v is PresenceState {
  if (v === null || typeof v !== 'object') return false;
  const u = (v as { user?: unknown }).user;
  return u !== null && typeof u === 'object' && typeof (u as { name?: unknown }).name === 'string';
}

/**
 * Remote peers from the awareness state map, without ourselves; the same user in several tabs
 * is shown once (most recent client id wins).
 */
export function peersFromStates(
  states: ReadonlyMap<number, unknown>,
  selfClientId: number,
  selfUserId?: string,
): Peer[] {
  const byUser = new Map<string, Peer>();
  for (const [clientId, raw] of states) {
    if (clientId === selfClientId || !isPresence(raw)) continue;
    if (selfUserId !== undefined && raw.user.id === selfUserId) continue;
    const peer: Peer = {
      clientId,
      user: raw.user,
      cursor: raw.cursor ?? null,
      selection: Array.isArray(raw.selection) ? raw.selection : [],
    };
    const prev = byUser.get(raw.user.id);
    if (!prev || prev.clientId < clientId) byUser.set(raw.user.id, peer);
  }
  return [...byUser.values()].sort((a, b) => a.user.name.localeCompare(b.user.name));
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
